import { Component, OnInit, computed, inject, model, signal, ChangeDetectionStrategy } from '@angular/core';
import { saisieDansLePlafond } from '../../global/plafond-acompte';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AlertComponent } from '@shared/components/alert';
import { ButtonComponent } from '@shared/components/button';
import { FieldErrorDirective, FieldHintDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { SelectComponent } from '@shared/components/select';
import { IbanAutoformatPipe } from '../../global/pipe/iban-autoformat.pipe';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import { CustomValidators } from '../../../../partage/forms/custom-validators';
import { suivreFormulaire } from '../../../../partage/forms/suivre-formulaire';
import { normaliserSaisieMontant } from '../../../../partage/forms/normalisation-saisie';
import { nombreDepuisSaisie, nombreDepuisSaisieOuRepli } from '../../../../partage/format/nombre';
import { CompteBancaireService } from './compte-bancaire.service';
import {
  CompteBancaire,
  CompteBancaireForm,
  DialogDataInput,
  DialogReturn,
} from './dialog-acompte-reddition.interface';
import { DevisePipe } from '../../../../partage/pipes/montant.pipe';

@Component({
  selector: 'app-dialog-acompte-reddition-validation',
  imports: [
    DevisePipe,
    ModalShellComponent,
    ModalFooterDirective,
    AlertComponent,
    ButtonComponent,
    FormFieldComponent,
    FieldErrorDirective,
    FieldHintDirective,
    InputComponent,
    SelectComponent,
    ReactiveFormsModule,
  ],
  providers: [CompteBancaireService],
  templateUrl: './dialog-acompte-reddition-validation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-acompte-reddition-validation.component.scss',
})
export class DialogAcompteRedditionValidationComponent implements OnInit {
  readonly dialogRef = inject(DialogRef);
  private compteBancaireService = inject(CompteBancaireService);
  readonly data = inject<DialogDataInput>(DIALOG_DATA);
  listeComtpeBancaire = this.compteBancaireService.compteBancaires;
  evenement = this.compteBancaireService.evenement;
  infoDialog = this.compteBancaireService.infosDialog;
  creerCompteBancaire = this.compteBancaireService.creerCompteBancaire;

  montantAcompte = new FormControl(
    { value: null, disabled: false },
    {
      nonNullable: true,
      validators: [Validators.required, CustomValidators.minNombre(1), this.validationMontantAcompte()],
    },
  );

  compteBancaire = new FormControl<CompteBancaire>(
    { value: {} as CompteBancaire, disabled: false },
    { validators: [this.valideCompteB()] },
  );

  compteBancaireForm: FormGroup<CompteBancaireForm> = new FormGroup<CompteBancaireForm>({
    nomTitulaire: new FormControl<string>(
      { value: '', disabled: false },
      {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3)],
      },
    ),
    iban: new FormControl<string>(
      { value: '', disabled: false },
      {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('^[A-Za-z]{2}[0-9]{2}(\\s?[A-Za-z0-9]{4}){2,8}(\\s?[A-Za-z0-9]{0,4})?$'),
          this.ValideIban(),
        ],
      },
    ),
    bic: new FormControl<string>(
      { value: '', disabled: false },
      {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('^[A-Za-z0-9]{4}\\s?[A-Za-z]{2}\\s?[A-Za-z0-9]{2}\\s?[A-Za-z0-9]{0,3}$'),
        ],
      },
    ),
  });

  // Lus par le gabarit : erreurs et validité notifiées à chaque validation
  protected readonly montantAcompteSuivi = suivreFormulaire(this.montantAcompte);
  protected readonly compteBancaireSuivi = suivreFormulaire(this.compteBancaire);
  protected readonly compteBancaireFormSuivi = suivreFormulaire(this.compteBancaireForm);

  // `InputType` du kit n'a pas `number` (manque A14)
  protected readonly chiffres = /[0-9.,]/;

  /* La colonne `MontantAcompte` est en `decimal(18, 2)` : une 3e decimale saisie etait
     arrondie a l'enregistrement sans que l'ecran le dise. On borne a la sortie du champ,
     ce qui remet d'accord ce qui est affiche, ce qui est valide contre le plafond, et ce
     qui est enregistre. */
  protected readonly normaliserMontant = normaliserSaisieMontant;

  protected titreModale = computed(() =>
    this.infoDialog().type === 'acompte' ? "Valider l'acompte" : 'Valider la reddition',
  );

  // Le déclencheur affichait le titulaire, l'option titulaire + IBAN : `label` et
  // `suffix` de `SelectOption` couvrent les deux sans gabarit personnalisé
  protected optionsComptes = computed<SelectOption[]>(() =>
    (this.listeComtpeBancaire() ?? []).map(compte => ({
      value: compte.compteBancaireId,
      label: compte.nomTitulaire,
      suffix: compte.iban,
    })),
  );

  protected comptesSelectionnes = computed<string[]>(() => {
    const id = this.compteBancaireCourant();
    return id ? [id] : [];
  });

  protected ibanSelectionne = computed(
    () => (this.listeComtpeBancaire() ?? []).find(c => c.compteBancaireId === this.compteBancaireCourant())?.iban ?? '',
  );

  private compteBancaireCourant = signal<string>('');

  protected choisirCompte(ids: readonly string[]): void {
    const id = ids[0] ?? '';
    this.compteBancaireCourant.set(id);
    const compte = (this.listeComtpeBancaire() ?? []).find(c => c.compteBancaireId === id);
    this.compteBancaire.setValue(compte ?? ({} as CompteBancaire));
  }

  protected annuler(): void {
    this.dialogRef.close(this.refuse());
  }

  ngOnInit() {
    this.compteBancaireService.dialogData.set(this.data);
  }

  constructor() {
    // Le formatage était appliqué à l'affichage seul (`[value]="… | ibanAutoformat"`),
    // ce qui laissait le contrôle et le champ diverger. On le pose maintenant SUR le
    // contrôle : les validateurs tolèrent les espaces (`\s?`) et le submit les retire.
    const formateur = new IbanAutoformatPipe();

    this.compteBancaireForm.controls.iban.valueChanges.subscribe(valeur => {
      const formate = formateur.transform(valeur ?? '');
      if (formate !== valeur) {
        this.compteBancaireForm.controls.iban.setValue(formate, { emitEvent: false });
      }
    });

    this.compteBancaireForm.controls.bic.valueChanges.subscribe(valeur => {
      const majuscules = (valeur ?? '').toUpperCase();
      if (majuscules !== valeur) {
        this.compteBancaireForm.controls.bic.setValue(majuscules, { emitEvent: false });
      }
    });
  }

  toggle() {
    this.creerCompteBancaire.set(!this.creerCompteBancaire());
  }

  isValid() {
    const test = this.compteBancaireSuivi().invalid;
    const test2 = this.listeComtpeBancaire() && this.listeComtpeBancaire()!.length === 0;
    const test3 = this.infoDialog().type === 'acompte' && this.montantAcompteSuivi().invalid;
    const test4 = this.infoDialog().actionEstBLoquee;

    return test || test2 || test3 || test4;
  }

  ajouterComtpeBancaire() {
    this.compteBancaireForm.markAllAsTouched();
    if (this.compteBancaireForm.valid) {
      const info = this.evenement();

      this.compteBancaireService.compteBancaireAjout.set({
        idClient: info.nomClient,
        iban: this.compteBancaireForm.value.iban!.replaceAll(' ', ''),
        bic: this.compteBancaireForm.value.bic!.replaceAll(' ', ''),
        nomTitulaire: this.compteBancaireForm.value.nomTitulaire!,
      });
    }
  }

  SubmitValue() {
    this.dialogRef.close({
      valide: true,
      montantAcompte: nombreDepuisSaisieOuRepli(this.montantAcompte?.value),
      idCompteBancaire: this.compteBancaire.value?.compteBancaireId, // this.id.value.replace(' ', ''),
    });
  }

  readonly refuse = model<DialogReturn>({
    valide: false,
    montantAcompte: 0,
    idCompteBancaire: '',
  });

  ValideIban(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const codeLengths = {
        AD: 24,
        AE: 23,
        AL: 28,
        AT: 20,
        AZ: 28,
        BA: 20,
        BE: 16,
        BG: 22,
        BH: 22,
        BR: 29,
        CH: 21,
        CR: 21,
        CY: 28,
        CZ: 24,
        DE: 22,
        DK: 18,
        DO: 28,
        EE: 20,
        ES: 24,
        LC: 30,
        FI: 18,
        FO: 18,
        FR: 27,
        GB: 22,
        GI: 23,
        GL: 18,
        GR: 27,
        GT: 28,
        HR: 21,
        HU: 28,
        IE: 22,
        IL: 23,
        IS: 26,
        IT: 27,
        JO: 30,
        KW: 30,
        KZ: 20,
        LB: 28,
        LI: 21,
        LT: 20,
        LU: 20,
        LV: 21,
        MC: 27,
        MD: 24,
        ME: 22,
        MK: 19,
        MR: 27,
        MT: 31,
        MU: 30,
        NL: 18,
        NO: 15,
        PK: 24,
        PL: 28,
        PS: 29,
        PT: 25,
        QA: 29,
        RO: 24,
        RS: 22,
        SA: 24,
        SE: 24,
        SI: 19,
        SK: 24,
        SM: 27,
        TN: 24,
        TR: 26,
      };
      if (control.value) {
        const value = control.value.replace(' ', '');
        const iban = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
        if (!code || iban.length !== (codeLengths as never)[code[1]]) {
          return { ValideIban: true };
        }
        const digits: number = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
          return letter.charCodeAt(0) - 55;
        });
        return this.mod97(digits) === 1 ? null : { ValideIban: true };
      }
      return null;
    };
  }

  valideCompteB(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as CompteBancaire;
      if (value.compteBancaireId) {
        return value.compteBancaireId === '' ? { valideCB: false } : null;
      }
      return { valideCB: false };
    };
  }

  validationMontantAcompte(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      /* Le kit pousse une CHAINE : sans conversion, `'1,50' <= 500` coerce en `NaN`
         et la comparaison est fausse — une saisie a la virgule etait rejetee comme
         depassant le plafond. `CustomValidators.minNombre` couvre le cas invalide. */
      const montant = nombreDepuisSaisie(control.value);
      if (montant === null) {
        return null;
      }

      return saisieDansLePlafond(montant, this.infoDialog().MontantMaxAcompte) ? null : { valideMontant: true };
    };
  }

  mod97(digital: number | string) {
    digital = digital.toString();
    let checksum: number | string = digital.slice(0, 2);
    let fragment = '';
    for (let offset = 2; offset < digital.length; offset += 7) {
      fragment = checksum + digital.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }
}
